import { useState, useEffect, useMemo, useCallback } from "react";
import { runQuery, runProcedure } from "../services/api";
import DataTable from "./DataTable";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell,
} from "recharts";

const COLORS = [
  "var(--accent)","#22c55e","#f59e0b","#a855f7","#ef4444",
  "#06b6d4","#f97316","#10b981","#8b5cf6","#ec4899",
];

const NOW    = new Date();
const CUR_YIL = NOW.getFullYear();
const CUR_AY  = NOW.getMonth() + 1;

function donem(yil, ay) {
  return `${yil}-${String(ay).padStart(2, "0")}`;
}
const CUR_DONEM = donem(CUR_YIL, CUR_AY);

function fmtNum(val) {
  if (val === null || val === undefined || isNaN(val)) return "—";
  const n = parseFloat(val);
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M ₺";
  if (Math.abs(n) >= 1_000)     return (n / 1_000).toFixed(1)     + "K ₺";
  return n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺";
}

function fmtAxis(val) {
  if (Math.abs(val) >= 1_000_000) return (val / 1_000_000).toFixed(1) + "M";
  if (Math.abs(val) >= 1_000)     return (val / 1_000).toFixed(0)     + "K";
  return val;
}

const tooltipStyle = {
  contentStyle: { background: "var(--bg-page)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "var(--text-primary)" },
};

// ── Yardımcı hesaplamalar ──────────────────────────────────────────────────────
function computeKPIs(rows) {
  let buAy = 0, geciken = 0, gelecek = 0;
  rows.forEach(({ YIL, AY, TUTAR }) => {
    const t = Number(TUTAR) || 0;
    const d = donem(YIL, AY);
    if (d === CUR_DONEM)      buAy    += t;
    else if (d < CUR_DONEM)   geciken += t;
    else                       gelecek += t;
  });
  return { buAy, geciken, gelecek };
}

function buildCariChartData(rows) {
  const map = {};
  rows.forEach(({ ISIM, TUTAR }) => {
    map[ISIM] = (map[ISIM] || 0) + (Number(TUTAR) || 0);
  });
  return Object.entries(map)
    .sort(([, a], [, b]) => b - a)
    .map(([isim, tutar]) => ({ isim, TUTAR: tutar }));
}

function buildDonemList(rows) {
  return [...new Set(rows.map(({ YIL, AY }) => donem(YIL, AY)))].sort();
}

function buildIsimPivot(rows) {
  const gelecekDonemler = [...new Set(
    rows
      .filter(({ YIL, AY }) => donem(YIL, AY) > CUR_DONEM)
      .map(({ YIL, AY }) => donem(YIL, AY))
  )].sort();

  const map = {};
  rows.forEach(({ ISIM, KOD, YIL, AY, TUTAR }) => {
    if (!map[ISIM]) {
      map[ISIM] = { ISIM, KOD: KOD || ISIM, TOPLAM: 0, BU_AY: 0, GECIKEN: 0 };
      gelecekDonemler.forEach((d) => { map[ISIM][d] = 0; });
    }
    const t = Number(TUTAR) || 0;
    const d = donem(YIL, AY);
    map[ISIM].TOPLAM += t;
    if (d === CUR_DONEM)    map[ISIM].BU_AY   += t;
    else if (d < CUR_DONEM) map[ISIM].GECIKEN  += t;
    else                     map[ISIM][d]       += t;
  });

  return {
    data: Object.values(map).sort((a, b) => b.TOPLAM - a.TOPLAM),
    gelecekDonemler,
  };
}

// ── Alt bileşenler ─────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, color, icon }) {
  return (
    <div style={{ background: "var(--bg-card)", borderRadius: 12, padding: "18px 20px", borderTop: `3px solid ${color}`, flex: 1, minWidth: 180 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 20 }}>{icon}</span>
        <span style={{ color: "var(--text-secondary)", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
      </div>
      <div style={{ color: "var(--text-heading)", fontSize: 22, fontWeight: 700 }}>{value}</div>
      {sub && <div style={{ color: "var(--text-faint)", fontSize: 12, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function IsimPivotTable({ data, gelecekDonemler, entityLabel, onMailClick, mailLoadingCari }) {
  const sabitCols = [
    { key: "ISIM",    label: entityLabel, align: "left" },
    { key: "TOPLAM",  label: "Toplam",    align: "right", color: () => "var(--text-primary)" },
    { key: "BU_AY",   label: "Bu Ay",     align: "right", color: () => "var(--accent-text)" },
    { key: "GECIKEN", label: "Geçiken",   align: "right", color: (v) => v > 0 ? "#fca5a5" : "var(--border)" },
  ];
  const gelecekCols = gelecekDonemler.map((d) => ({
    key: d, label: d, align: "right",
    color: (v) => v > 0 ? "#86efac" : "var(--border)",
    isGelecek: true,
  }));
  const allCols = [...sabitCols, ...gelecekCols];
  const totals = {};
  allCols.forEach(({ key }) => {
    if (key !== "ISIM") totals[key] = data.reduce((s, r) => s + (r[key] || 0), 0);
  });

  return (
    <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid var(--border)" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          {gelecekCols.length > 0 && (
            <tr style={{ background: "var(--bg-page)" }}>
              <th colSpan={sabitCols.length} style={{ padding: "6px 14px", borderBottom: "1px solid var(--accent-bg)", borderRight: "2px solid var(--border)" }} />
              <th colSpan={gelecekCols.length} style={{ padding: "6px 14px", textAlign: "center", color: "#86efac", fontSize: 11, fontWeight: 700, letterSpacing: 1, borderBottom: "1px solid var(--accent-bg)", textTransform: "uppercase" }}>
                Gelecek Dönemler
              </th>
            </tr>
          )}
          <tr style={{ background: "var(--bg-card)" }}>
            {allCols.map((c, ci) => (
              <th key={c.key} style={{ padding: "10px 14px", textAlign: c.align, color: c.isGelecek ? "#86efac" : "var(--text-secondary)", fontWeight: 600, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", borderRight: ci === sabitCols.length - 1 ? "2px solid var(--border)" : undefined }}>
                {c.label}
              </th>
            ))}
            {onMailClick && <th style={{ padding: "10px 14px", borderBottom: "1px solid var(--border)", width: 60 }} />}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? "var(--bg-page)" : "var(--bg-alt-row)" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--accent-bg)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? "var(--bg-page)" : "var(--bg-alt-row)")}
            >
              {allCols.map((c, ci) => {
                if (c.key === "ISIM") return (
                  <td key="ISIM" title={row.ISIM} style={{ padding: "8px 14px", color: "var(--text-primary)", borderBottom: "1px solid var(--border-hairline)", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", borderRight: "2px solid var(--border)" }}>
                    {row.ISIM}
                  </td>
                );
                const val = row[c.key] || 0;
                return (
                  <td key={c.key} style={{ padding: "8px 14px", textAlign: "right", color: c.color(val), borderBottom: "1px solid var(--border-hairline)", whiteSpace: "nowrap", borderRight: ci === sabitCols.length - 1 ? "2px solid var(--border)" : undefined }}>
                    {val > 0 ? fmtNum(val) : <span style={{ color: "var(--text-ghost)" }}>—</span>}
                  </td>
                );
              })}
              {onMailClick && (
                <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border-hairline)", textAlign: "center" }}>
                  <button
                    onClick={() => onMailClick(row.KOD, row.ISIM)}
                    disabled={mailLoadingCari === row.KOD}
                    title={`${row.ISIM} için ödeme iste`}
                    style={{
                      background: mailLoadingCari === row.KOD ? "#3f1515" : "#2d0a0a",
                      border: "1px solid #7f1d1d", borderRadius: 6,
                      color: "#fca5a5", fontSize: 11, cursor: "pointer",
                      padding: "3px 8px", fontWeight: 600,
                    }}
                  >
                    {mailLoadingCari === row.KOD ? "⏳" : "📧"}
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: "var(--accent-bg)" }}>
            {allCols.map((c, ci) => {
              if (c.key === "ISIM") return (
                <td key="ISIM" style={{ padding: "9px 14px", color: "var(--accent-text)", fontWeight: 700, fontSize: 13, borderTop: "2px solid var(--border)", borderRight: "2px solid var(--border)" }}>TOPLAM</td>
              );
              const val = totals[c.key] || 0;
              return (
                <td key={c.key} style={{ padding: "9px 14px", textAlign: "right", color: c.isGelecek ? "#86efac" : c.key === "GECIKEN" ? "#fca5a5" : "var(--accent-text)", fontWeight: 700, borderTop: "2px solid var(--border)", whiteSpace: "nowrap", borderRight: ci === sabitCols.length - 1 ? "2px solid var(--border)" : undefined }}>
                  {val > 0 ? fmtNum(val) : "—"}
                </td>
              );
            })}
            {onMailClick && <td style={{ borderTop: "2px solid var(--border)" }} />}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function GrafikSekmesi({ rows, isimler }) {
  const TUM = "__TUM__";
  const [secili, setSecili] = useState(TUM);

  const kaynakRows = useMemo(() =>
    secili === TUM ? rows : rows.filter((r) => r.ISIM === secili),
    [rows, secili]
  );

  const chartData = useMemo(() => {
    const map = {};
    kaynakRows.forEach(({ YIL, AY, TUTAR }) => {
      const key = donem(YIL, AY);
      map[key] = (map[key] || 0) + (Number(TUTAR) || 0);
    });
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([d, tutar]) => ({ donem: d, TUTAR: tutar }));
  }, [kaynakRows]);

  const kpi = useMemo(() => {
    let gecmis = 0, buAy = 0, gelecek = 0;
    kaynakRows.forEach(({ YIL, AY, TUTAR }) => {
      const t = Number(TUTAR) || 0;
      const d = donem(YIL, AY);
      if (d < CUR_DONEM)      gecmis  += t;
      else if (d === CUR_DONEM) buAy  += t;
      else                      gelecek += t;
    });
    return { gecmis, buAy, gelecek, toplam: gecmis + buAy + gelecek };
  }, [kaynakRows]);

  const baslik = secili === TUM
    ? "Tüm Cariler — Aylık Toplam Tutar"
    : `${secili} — Aylık Tutar`;

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      {/* Sol: cari listesi */}
      <div style={{ width: 220, flexShrink: 0, background: "var(--bg-page)", borderRadius: 10, border: "1px solid var(--border-hairline)", display: "flex", flexDirection: "column", maxHeight: 520 }}>
        <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--border-hairline)", color: "var(--text-muted)", fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
          Cari Seç
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          <button onClick={() => setSecili(TUM)} style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", border: "none", borderBottom: "1px solid var(--border-hairline)", background: secili === TUM ? "var(--accent-bg)" : "transparent", color: secili === TUM ? "var(--accent-text)" : "var(--text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: secili === TUM ? 700 : 400 }}>
            🌐 Tüm Cariler
          </button>
          {isimler.map((isim) => (
            <button key={isim} onClick={() => setSecili(isim)} title={isim} style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", border: "none", borderBottom: "1px solid var(--bg-alt-row)", background: secili === isim ? "var(--accent-bg)" : "transparent", color: secili === isim ? "var(--accent-text)" : "var(--text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: secili === isim ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {isim}
            </button>
          ))}
        </div>
      </div>

      {/* Sağ: KPI + grafik */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 14 }}>
          {[
            { label: "Vadesi Geçmiş",  value: kpi.gecmis,  color: "#ef4444", icon: "⚠️" },
            { label: "Bu Ay",          value: kpi.buAy,    color: "#f59e0b", icon: "📅" },
            { label: "Vadesi Gelecek", value: kpi.gelecek, color: "#22c55e", icon: "🔮" },
            { label: "Toplam",         value: kpi.toplam,  color: "var(--accent)", icon: "💰" },
          ].map(({ label, value, color, icon }) => (
            <div key={label} style={{ background: "var(--bg-card)", borderRadius: 10, padding: "12px 14px", borderTop: `3px solid ${color}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 14 }}>{icon}</span>
                <span style={{ color: "var(--text-muted)", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</span>
              </div>
              <div style={{ color: value > 0 ? color : "var(--text-ghost)", fontSize: 17, fontWeight: 700 }}>
                {value > 0 ? fmtNum(value) : "—"}
              </div>
            </div>
          ))}
        </div>

        <div style={{ background: "var(--bg-card)", borderRadius: 10, padding: "18px 20px" }}>
          <div style={{ color: "var(--text-secondary)", fontSize: 13, fontWeight: 600, marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{baslik}</div>
          <div style={{ color: "var(--text-faint)", fontSize: 12, marginBottom: 16 }}>{chartData.length} dönem</div>
          {chartData.length === 0 ? (
            <div style={{ color: "var(--text-ghost)", fontSize: 14, textAlign: "center", padding: "40px 0" }}>Bu cari için veri bulunamadı</div>
          ) : (
            <ResponsiveContainer width="100%" height={380}>
              <BarChart data={chartData} margin={{ top: 0, right: 16, bottom: 48, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="donem" tick={{ fill: "var(--text-muted)", fontSize: 11 }} angle={-40} textAnchor="end" />
                <YAxis tick={{ fill: "var(--text-muted)", fontSize: 11 }} tickFormatter={fmtAxis} width={72} />
                <Tooltip {...tooltipStyle} formatter={(v) => [fmtNum(v), "Tutar"]} labelFormatter={(l) => `Dönem: ${l}`} />
                <Bar dataKey="TUTAR" radius={[4, 4, 0, 0]} maxBarSize={48}>
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.donem < CUR_DONEM ? "#ef4444" : entry.donem === CUR_DONEM ? "#f59e0b" : "#22c55e"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap" }}>
            {[["#ef4444","Geçmiş"],["#f59e0b","Bu Ay"],["#22c55e","Gelecek"]].map(([c, l]) => (
              <div key={l} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: c }} />
                <span style={{ color: "var(--text-muted)", fontSize: 11 }}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Ana Bileşen (config ile çalışır) ──────────────────────────────────────────
export default function YaslandirmaReport({ config }) {
  const { sql, procedure, title, icon, entityLabel, tabLabel } = config;

  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("ozet");
  const [selectedDonem, setSelectedDonem] = useState(null);
  const [procState, setProcState] = useState("idle");
  const [mailLoadingCari, setMailLoadingCari] = useState(null);
  const [mailMsg, setMailMsg] = useState(null);
  const [mailModal, setMailModal] = useState(null); // { cariKodu, cariIsmi } veya null
  const [mailTo, setMailTo] = useState("");
  const [mailCc, setMailCc] = useState("finans@emkyazilim.com");
  const [procMsg, setProcMsg] = useState("");

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    runQuery(sql)
      .then((r) => setRows(r.data.rows))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [sql]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRunProcedure = async () => {
    setProcState("running");
    setProcMsg("");
    try {
      await runProcedure(procedure);
      setProcState("done");
      setProcMsg("Prosedür başarıyla çalıştı. Veriler yenileniyor...");
      fetchData();
      setTimeout(() => setProcState("idle"), 4000);
    } catch (e) {
      setProcState("error");
      setProcMsg(e.response?.data?.detail || e.message);
    }
  };

  const handleOdemeIsteMail = (cariKodu = null, cariIsmi = null) => {
    if (cariKodu) {
      // Cari bazlı: modal aç
      setMailTo("");
      setMailCc("finans@emkyazilim.com");
      setMailModal({ cariKodu, cariIsmi });
    } else {
      // Tümü: direkt gönder
      handleOdemeIsteGonder(null, null, null);
    }
  };

  const handleOdemeIsteGonder = async (cariKodu, toEmail, ccEmail) => {
    const key = cariKodu || "tumu";
    setMailLoadingCari(key);
    setMailModal(null);
    setMailMsg(null);
    try {
      const params = new URLSearchParams();
      if (cariKodu) params.append("cari_kodu", cariKodu);
      if (toEmail) params.append("to_email", toEmail);
      if (ccEmail) params.append("cc_email", ccEmail);
      const res = await fetch(
        `/api/contracts/alici-borc/odeme-iste?${params.toString()}`,
        { method: "POST" }
      );
      const data = await res.json();
      setMailMsg({ text: data.mesaj, type: "success" });
    } catch (e) {
      setMailMsg({ text: "Mail gönderilemedi: " + e.message, type: "error" });
    } finally {
      setMailLoadingCari(null);
      setTimeout(() => setMailMsg(null), 5000);
    }
  };

  const kpis = useMemo(() => rows ? computeKPIs(rows) : null, [rows]);

  const { data: isimToplam, gelecekDonemler } = useMemo(
    () => rows ? buildIsimPivot(rows) : { data: [], gelecekDonemler: [] },
    [rows]
  );

  const cariChartData = useMemo(() => rows ? buildCariChartData(rows) : [], [rows]);

  const donemList = useMemo(() => rows ? buildDonemList(rows) : [], [rows]);

  const filteredRawRows = useMemo(() => {
    if (!rows) return [];
    if (!selectedDonem) return rows;
    return rows.filter((r) => donem(r.YIL, r.AY) === selectedDonem);
  }, [rows, selectedDonem]);

  const rawTableRows = useMemo(() =>
    filteredRawRows.map((r) => ({
      ISIM: r.ISIM,
      DONEM: donem(r.YIL, r.AY),
      "FATURA NO": r.BELGE_NO || "—",
      "FATURA TARİHİ": r.FATURA_TARIHI ? String(r.FATURA_TARIHI).slice(0, 10) : "—",
      "VADE TARİHİ": r.VADE_TARIHI ? String(r.VADE_TARIHI).slice(0, 10) : "—",
      TUTAR: Number(r.TUTAR).toLocaleString("tr-TR", { minimumFractionDigits: 2 }) + " ₺",
    })), [filteredRawRows]);

  if (loading) return <div style={{ color: "var(--text-muted)", fontSize: 14, padding: "40px 0", textAlign: "center" }}>⏳ Veri yükleniyor...</div>;
  if (error)   return <div style={{ background: "#450a0a", border: "1px solid #7f1d1d", borderRadius: 8, padding: "12px 16px", color: "#fca5a5", fontSize: 13 }}>{error}</div>;

  const TABS = [
    { id: "ozet",   label: "📋 Özet" },
    { id: "grafik", label: "📊 Grafik" },
    { id: "entity", label: `🏢 ${tabLabel}` },
    { id: "detay",  label: "🔍 Detay" },
  ];

  return (
    <div>
      {/* Mail Modal */}
      {mailModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "var(--bg-page)", border: "1px solid var(--accent-bg)", borderRadius: 12, width: 480, padding: 28, boxShadow: "0 25px 50px rgba(0,0,0,0.5)" }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--accent-text)", marginBottom: 6 }}>📧 Ödeme Talebi Gönder</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 20 }}>{mailModal.cariIsmi}</div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>Alıcı (To) *</div>
              <input
                autoFocus
                type="email"
                placeholder="ornek@firma.com"
                value={mailTo}
                onChange={e => setMailTo(e.target.value)}
                style={{ width: "100%", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 7, padding: "9px 12px", color: "var(--text-primary)", fontSize: 13, outline: "none", boxSizing: "border-box" }}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>CC (isteğe bağlı)</div>
              <input
                type="email"
                value={mailCc}
                onChange={e => setMailCc(e.target.value)}
                style={{ width: "100%", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 7, padding: "9px 12px", color: "var(--text-primary)", fontSize: 13, outline: "none", boxSizing: "border-box" }}
              />
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setMailModal(null)}
                style={{ padding: "9px 18px", borderRadius: 7, border: "1px solid var(--border)", background: "transparent", color: "var(--text-muted)", fontSize: 13, cursor: "pointer" }}>
                İptal
              </button>
              <button
                disabled={!mailTo.trim()}
                onClick={() => handleOdemeIsteGonder(mailModal.cariKodu, mailTo.trim(), mailCc.trim())}
                style={{ padding: "9px 20px", borderRadius: 7, border: "none", background: !mailTo.trim() ? "var(--border)" : "#7f1d1d", color: !mailTo.trim() ? "var(--text-muted)" : "#fca5a5", fontSize: 13, fontWeight: 600, cursor: !mailTo.trim() ? "not-allowed" : "pointer" }}>
                📧 Gönder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Başlık + Prosedür Butonu */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24, gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "var(--text-heading)" }}>{icon} {title}</div>
          <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>
            {rows.length} kayıt · {isimToplam.length} farklı {entityLabel.toLowerCase()} · {donemList.length} dönem
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          {mailMsg && (
            <div style={{ fontSize: 12, color: mailMsg.type === "error" ? "#fca5a5" : "#86efac", maxWidth: 320, textAlign: "right", marginBottom: 4 }}>
              {mailMsg.text}
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            {config.showOdemeIste !== false && (
              <button
                onClick={() => handleOdemeIsteMail(null)}
                disabled={mailLoadingCari === "tumu"}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 20px", borderRadius: 8, border: "none",
                  background: mailLoadingCari === "tumu" ? "#3f1515" : "#7f1d1d",
                  color: "#fca5a5", fontWeight: 600, fontSize: 14,
                  cursor: mailLoadingCari === "tumu" ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {mailLoadingCari === "tumu" ? "⏳ Gönderiliyor..." : "📧 Tümüne Ödeme İste"}
              </button>
            )}
          <button
            onClick={handleRunProcedure}
            disabled={procState === "running" || loading}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "10px 20px", borderRadius: 8, border: "none",
              background: procState === "running" ? "var(--accent-bg)" : procState === "error" ? "#450a0a" : procState === "done" ? "#14532d" : "#1d4ed8",
              color: procState === "error" ? "#fca5a5" : procState === "done" ? "#86efac" : "#fff",
              fontWeight: 600, fontSize: 14,
              cursor: procState === "running" || loading ? "not-allowed" : "pointer",
              transition: "background 0.2s", whiteSpace: "nowrap",
            }}
          >
            <span style={{ fontSize: 16 }}>
              {procState === "running" ? "⏳" : procState === "done" ? "✅" : procState === "error" ? "❌" : "▶"}
            </span>
            {procState === "running" ? "Prosedür Çalışıyor..." : "Yaşlandırma Prosedürünü Çalıştır"}
          </button>
          {procMsg && <div style={{ fontSize: 12, color: procState === "error" ? "#fca5a5" : "#86efac", maxWidth: 320, textAlign: "right" }}>{procMsg}</div>}
          </div>
        </div>
      </div>

      {/* KPI Kartlar */}
      <div style={{ display: "flex", gap: 14, marginBottom: 28, flexWrap: "wrap" }}>
        <KpiCard icon="📅" label="Bu Ay Toplam"    value={fmtNum(kpis.buAy)}    sub={`${CUR_YIL} / ${String(CUR_AY).padStart(2,"0")}`} color="var(--accent)" />
        <KpiCard icon="⚠️" label="Geçiken Toplam"  value={fmtNum(kpis.geciken)} sub="Geçmiş dönemler"  color="#ef4444" />
        <KpiCard icon="🔮" label="Gelecek Toplam"  value={fmtNum(kpis.gelecek)} sub="İleri dönemler"   color="#22c55e" />
        <KpiCard icon="💰" label="Genel Toplam"    value={fmtNum(kpis.buAy + kpis.geciken + kpis.gelecek)} sub={`${isimToplam.length} ${entityLabel.toLowerCase()}`} color="#f59e0b" />
      </div>

      {/* Sekmeler */}
      <div style={{ display: "flex", borderBottom: "1px solid var(--border)", marginBottom: 20 }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ padding: "9px 18px", background: "transparent", border: "none", borderBottom: activeTab === t.id ? "2px solid var(--accent)" : "2px solid transparent", color: activeTab === t.id ? "var(--accent)" : "var(--text-muted)", cursor: "pointer", fontWeight: activeTab === t.id ? 600 : 400, fontSize: 14 }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Özet */}
      {activeTab === "ozet" && (
        <div style={{ background: "var(--bg-card)", borderRadius: 10, padding: "18px 20px" }}>
          <div style={{ color: "var(--text-secondary)", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            {entityLabel} Bazlı Toplam Tutar
          </div>
          <div style={{ color: "var(--text-faint)", fontSize: 12, marginBottom: 16 }}>
            {cariChartData.length} {entityLabel.toLowerCase()} · toplam {fmtNum(cariChartData.reduce((s, d) => s + d.TUTAR, 0))}
          </div>
          <ResponsiveContainer width="100%" height={Math.max(260, cariChartData.length * 36)}>
            <BarChart
              data={cariChartData}
              layout="vertical"
              margin={{ top: 0, right: 80, bottom: 0, left: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
              <XAxis type="number" tick={{ fill: "var(--text-muted)", fontSize: 11 }} tickFormatter={fmtAxis} />
              <YAxis
                type="category"
                dataKey="isim"
                width={220}
                tick={{ fill: "var(--text-secondary)", fontSize: 11 }}
                tickFormatter={(v) => v.length > 28 ? v.slice(0, 28) + "…" : v}
              />
              <Tooltip
                {...tooltipStyle}
                formatter={(v) => [fmtNum(v), "Toplam Tutar"]}
                labelFormatter={(l) => l}
              />
              <Bar dataKey="TUTAR" radius={[0, 4, 4, 0]} maxBarSize={28}>
                {cariChartData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Grafik */}
      {activeTab === "grafik" && (
        <GrafikSekmesi rows={rows} isimler={isimToplam.map((r) => r.ISIM)} />
      )}

      {/* Entity Bazlı (Alıcı / Satıcı) */}
      {activeTab === "entity" && (
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
            <div style={{ color: "var(--text-muted)", fontSize: 12 }}>
              Geçiken <span style={{ color: "#fca5a5" }}>kırmızı</span> · Gelecek aylar <span style={{ color: "#86efac" }}>yeşil</span> · {gelecekDonemler.length} gelecek dönem
            </div>
            {config.showOdemeIste !== false && (
              <button
                onClick={() => handleOdemeIsteMail(null)}
                disabled={mailLoadingCari === "tumu"}
                style={{
                  display: "flex", alignItems: "center", gap: 7,
                  padding: "8px 16px", borderRadius: 7, border: "1px solid #7f1d1d",
                  background: mailLoadingCari === "tumu" ? "#3f1515" : "#2d0a0a",
                  color: "#fca5a5", fontSize: 13, fontWeight: 600, cursor: "pointer",
                }}
              >
                {mailLoadingCari === "tumu" ? "⏳ Gönderiliyor..." : "📧 Tümüne Ödeme İste"}
              </button>
            )}
          </div>
          <IsimPivotTable
            data={isimToplam}
            gelecekDonemler={gelecekDonemler}
            entityLabel={entityLabel}
            onMailClick={config.showOdemeIste !== false ? handleOdemeIsteMail : null}
            mailLoadingCari={mailLoadingCari}
          />
        </div>
      )}

      {/* Detay */}
      {activeTab === "detay" && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <span style={{ color: "var(--text-secondary)", fontSize: 13 }}>Dönem filtrele:</span>
            <select
              value={selectedDonem || ""}
              onChange={(e) => setSelectedDonem(e.target.value || null)}
              style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text-primary)", padding: "6px 10px", fontSize: 13, outline: "none" }}
            >
              <option value="">Tüm dönemler</option>
              {donemList.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <span style={{ color: "var(--text-faint)", fontSize: 13 }}>{filteredRawRows.length} kayıt</span>
          </div>
          <DataTable columns={["ISIM","DONEM","FATURA NO","FATURA TARİHİ","VADE TARİHİ","TUTAR"]} rows={rawTableRows} total={rawTableRows.length} limit={rawTableRows.length} offset={0} />
        </div>
      )}
    </div>
  );
}
